import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import RangeSlider from "./RangeSlider";
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Popper,
  ClickAwayListener,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

const RangeSliderDropdown = ({
  title,
  min,
  max,
  histogramValues,
  range,
  setRange,
  userSet,
}: {
  histogramValues: number[];
  title: string;
  min: number;
  max: number;
  range: number[];
  setRange: (range: number[]) => void;
  userSet?: boolean;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [tempRange, setTempRange] = useState<number[]>(range);

  const selectedFilters = userSet !== undefined ? userSet : (range[0] !== min || range[1] !== max);
  const buttonTitle =
    selectedFilters && title === "Price"
      ? `from ${range[0]}€ - to ${range[1]}€`
      : selectedFilters && title === "Year"
        ? `year ${range[0]} - ${range[1]}`
        : selectedFilters && title === "Length"
          ? `${range[0]}ft - ${range[1]}ft`
          : title;

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setRange([min, max]);
    setTempRange([min, max]);
  };

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobile) {
      setOpen(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
    setTempRange(range);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleSave = () => {
    setRange(tempRange);
    handleClose();
  };

  const sliderContent = (
    <>
      <RangeSlider
        range={tempRange}
        setRange={setTempRange}
        histogramValues={histogramValues}
        title={title}
        max={max}
        min={min}
      />
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          onClick={handleClear}
          sx={{ textTransform: "capitalize", textDecoration: "underline", cursor: "pointer" }}
        >
          Clear
        </Typography>
        <Button
          variant="contained"
          sx={{
            borderRadius: "10px",
            backgroundColor: "black",
            color: "white",
            px: 2,
            "&:hover": { backgroundColor: "#333" },
          }}
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>
    </>
  );

  return (
    <Box>
      <Button
        onClick={handleOpen}
        variant="outlined"
        sx={{
          border: selectedFilters ? "2px solid #1565c0" : "1px solid #bdbdbd",
          borderRadius: "50px",
          px: 2,
          textTransform: "capitalize",
          color: selectedFilters ? "#1565c0" : "black",
          display: "flex",
          alignItems: "center",
          backgroundColor: selectedFilters ? "#e3f2fd" : "white",
          "&:hover": { border: "1px solid black", backgroundColor: "white" },
          "&:focus": { border: "2px solid #1565c0", backgroundColor: "#e3f2fd", color: "#1565c0" },
        }}
      >
        {buttonTitle}
        {selectedFilters && (
          <Box
            component="span"
            onClick={handleClear}
            sx={{ ml: 1, display: "flex", alignItems: "center", color: "#1565c0" }}
          >
            <CloseIcon fontSize="small" />
          </Box>
        )}
      </Button>

      {/* Mobile: bottom drawer anchored to viewport — no position jump */}
      <Drawer
        anchor="bottom"
        open={isMobile && open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "16px 16px 0 0",
            px: 3,
            pt: 1,
            pb: 4,
          },
        }}
      >
        {/* drag handle */}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, backgroundColor: "#ddd" }} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography fontWeight="bold" fontSize="16px">{title}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {sliderContent}
      </Drawer>

      {/* Desktop: Popper anchored to button */}
      {!isMobile && (
        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: 999 }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              sx={{
                width: "320px",
                boxShadow: 3,
                borderRadius: "10px",
                px: "20px",
                py: "10px",
              }}
            >
              {sliderContent}
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </Box>
  );
};

export default RangeSliderDropdown;
